import BigNumber from 'bignumber.js';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { services, SupportedChainId } from '@orionprotocol/sdk';
import z from 'zod';

import { Vault } from '../../types';
import { User, UserQuery, VeLumia } from '../../api/graphql/generated/graphql';

const { listPoolV2ResponseSchema } = services.indexer.schemas;

const { veORNInfoResponseSchema, votingInfoResponseSchema } =
  services.indexer.schemas;

export type PoolsV2Response = z.infer<typeof listPoolV2ResponseSchema>;
export type GovernancePoolsV2 = PoolsV2Response['result'];

export type VeOrnInfoData = z.infer<typeof veORNInfoResponseSchema>;

export type VotingInfoData = z.infer<typeof votingInfoResponseSchema>;
export type VotingPool = VotingInfoData['result']['pools'][0];

type LoadableData = {
  isLoading: boolean;
};

export type VeOrnInfo = LoadableData &
  Omit<VeOrnInfoData['result'], 'veTokenAddress'> & {
    veTokenAddress: VeOrnInfoData['result']['veTokenAddress'] | undefined;
  };

export type LumiaPInfo = {
  user: User | null;
  veLumia: VeLumia | null;
};

export type CurrentVeOrnInfo = Partial<{
  currentTotalVeOrn: string;
  currentUserVeOrn: string;
}>;

export type VotingInfo = LoadableData &
  VotingInfoData['result'] & { unusedWeightPercentBN: BigNumber | undefined };

export type GovernanceChainInfo = {
  isChainSupported: boolean;
};

export type GovernanceEnvironment = {
  chainId: number;
  nativeToken: string;
  WETH9: string;
  ORN: string;
  OrionV3Factory?: string;
  OrionV2Factory?: string;
  OrionV3NFTManager?: string;
  SwapRouterV3?: string;
  OrionFarmV3?: string;
  OrionFarmV2?: string;
  OrionVoting?: string;
  veORN?: string;
};

export type GovernanceState = {
  governanceEnvironments: Partial<
    Record<SupportedChainId, GovernanceEnvironment>
  >;
  vaultList: Vault[];
  availableUnstakeBalance: string;
  pools: Partial<Record<SupportedChainId, GovernancePoolsV2>>;
  isPoolsLoading: boolean;
  lumiaPInfo: UserQuery;
  veOrnInfo: VeOrnInfo;
  currentVeOrnInfo: CurrentVeOrnInfo;
  votingInfo: VotingInfo;
  chainsInfo: Partial<
    Record<SupportedChainId, { isLoading: boolean; data?: GovernanceChainInfo }>
  >;
};

const initialState: GovernanceState = {
  governanceEnvironments: {},
  vaultList: [],
  availableUnstakeBalance: '0',
  pools: {},
  isPoolsLoading: true,
  lumiaPInfo: {
    user: {
      blockNumberCreatedAt: '',
      blockNumberUpdatedAt: '',
      id: '',
      updatedAt: '',
      userLockEndDate: '',
      userLumiaLocked: '',
      userLumiaP_t0: '',
    },
    veLumia: {
      contract: '',
      totalLumiaLocked: '',
    },
  },
  veOrnInfo: {
    isLoading: false,
    avgAPR: 0,
    minAPR: 0,
    maxAPR: 0,
    veTokenAddress: undefined,
    totalORNLocked: 0,
    totalVeORN: 0,
    weeklyReward: 0,
    userAPR: 0,
    userVeORN: 0,
    userORNLocked: 0,
    userLockEndDate: 0,
    userReward: 0,
    userWeeklyReward: 0,
    userMinLockPeriod: 0,
  },
  currentVeOrnInfo: {},
  votingInfo: {
    isLoading: false,
    absoluteVeTokenInVoting: 0,
    pools: [],
    userVeTokenBalance: 0,
    userVeTokenInVoting: 0,
    veTokenAddress: '',
    votingAddress: '',
    weeklyReward: 0,
    unusedWeightPercentBN: undefined,
  },
  chainsInfo: {},
};

export const governanceSlice = createSlice({
  name: 'governance',
  initialState,
  reducers: {
    setVaultList(state, action: PayloadAction<Vault[]>) {
      state.vaultList = action.payload;
    },
    addVault(state, action: PayloadAction<Vault>) {
      state.vaultList = [...state.vaultList, action.payload];
    },
    removeVault(state, action: PayloadAction<number>) {
      state.vaultList = state.vaultList.filter(
        (vault) => vault.createdTime !== action.payload,
      );
    },
    setAvailableUnstakeBalance(
      state,
      action: PayloadAction<{
        value: string;
        operation: 'set' | 'plus' | 'minus';
      }>,
    ) {
      const balance = state.availableUnstakeBalance;
      const { value, operation } = action.payload;

      if (operation === 'set') state.availableUnstakeBalance = value;
      if (operation === 'plus')
        state.availableUnstakeBalance = new BigNumber(balance)
          .plus(value)
          .toString();
      if (operation === 'minus')
        state.availableUnstakeBalance = new BigNumber(balance)
          .minus(value)
          .toString();
    },

    setGovernanceEnvironment(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: GovernanceEnvironment;
      }>,
    ) {
      const { chainId, data } = action.payload;

      state.governanceEnvironments[chainId] = data;
    },

    setPools(
      state,
      action: PayloadAction<{
        data: GovernancePoolsV2;
        chainId: SupportedChainId;
        appChainId?: SupportedChainId;
      }>,
    ) {
      const { chainId, data, appChainId } = action.payload;

      state.pools[chainId] = data;

      if (chainId === appChainId) state.isPoolsLoading = false;
    },
    setIsPoolsLoading(state, action: PayloadAction<boolean>) {
      state.isPoolsLoading = action.payload;
    },

    setLumiaPInfo(state, action: PayloadAction<UserQuery>) {
      state.lumiaPInfo = { ...state.lumiaPInfo, ...action.payload };
    },
    setVeOrnInfo(state, action: PayloadAction<Partial<VeOrnInfo>>) {
      state.veOrnInfo = { ...state.veOrnInfo, ...action.payload };
    },
    setUserReward(state, action: PayloadAction<VeOrnInfo['userReward']>) {
      state.veOrnInfo = { ...state.veOrnInfo, userReward: action.payload };
    },
    resetVeOrnInfo(state) {
      state.veOrnInfo = initialState.veOrnInfo;
    },

    setCurrentVeOrnInfo(state, action: PayloadAction<CurrentVeOrnInfo>) {
      state.currentVeOrnInfo = { ...state.currentVeOrnInfo, ...action.payload };
    },

    setVotingInfo(state, action: PayloadAction<Partial<VotingInfo>>) {
      state.votingInfo = { ...state.votingInfo, ...action.payload };
    },
    resetVotingInfo(state) {
      state.votingInfo = initialState.votingInfo;
    },

    setChainInfo(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: GovernanceChainInfo;
      }>,
    ) {
      const { chainId, data } = action.payload;

      state.chainsInfo[chainId] = { isLoading: false, data };
    },
    setChainInfoIsLoading(
      state,
      action: PayloadAction<{ chainId: SupportedChainId }>,
    ) {
      const { chainId } = action.payload;

      state.chainsInfo[chainId] = { isLoading: true };
    },
  },
});

export const governanceReducer = governanceSlice.reducer;
